<template lang="pug">
    section.abonement-global-contents(v-loading="loading")
        template(v-if="!loading && initialized")
            .containers
                .page-area.container-item(v-for="(container, index) in containers" :key="container.id" :style="{ '--nesting-level': 1 }")
                    .area-title-bar
                        h4.title {{ getPositionLabel(container) }}
                        .settings
                            el-button.action.settings.info.icon-only(
                                size="mini"
                                type="text"
                                @click.stop="openContainerSettings(container)"
                            )
                                i.el-icon-fa-cogs.fas
                        .grow
                        .actions
                            el-button.delete-section.action.danger.icon-only(
                                size="mini"
                                type="text"
                                @click.stop="deleteContainer(container)"
                            )
                                i.el-icon-fa-trash-alt.fas
                    .container-body
                        Area(
                            v-if="container.gridAreas && container.gridAreas.length"
                            :config="getChildAreaConfig(container)"
                            :hide="false"
                            :dragging="dragging"
                            :sections-address="getChildSectionsAddress(container)"
                            :area-section-options="childSectionOptions"
                            :area-grid-options="[]"
                            @dragging="value => dragging = value"
                        )
            .add-container
                el-button(
                    round
                    size="small"
                    icon="el-icon-fa-plus fas"
                    @click="addContainer"
                    :loading="creatingContainer"
                ) {{ $t('Add Global Content') }}

        el-drawer.full-height-drawer(
            :title="$t('Settings')"
            :visible.sync="settingsDrawerVisible"
            :append-to-body="true"
            size="600px"
        )
            .drawer-body.no-tabs(v-if="editingContainer")
                .block-settings
                    .settings-wrap
                        .custom-settings
                            template(v-if="effectiveExtensions.includes('restricted-area')")
                                .setting.gc-restriction.flex-row
                                    el-form-item(:label="$t('Accessible by')" size="mini")
                                        el-select.is-full-width(placeholder="" v-model="restriction")
                                            el-option(
                                                v-for="role in restrictions"
                                                :key="role.key"
                                                :label="role.name"
                                                :value="role.value"
                                            )
                                    transition(name="slide-decent-left")
                                        el-form-item.grow(
                                            v-if="['members', 'member-tags'].includes(restriction)"
                                            label=" "
                                            size="mini"
                                        )
                                            el-switch(
                                                :active-text="$t('Any')"
                                                :value="restriction === 'members'"
                                                @change="toggleMemberRestriction"
                                            )
                                transition(name="slide-decent-vertical")
                                    .setting.gc-restriction-member-tags(v-if="restriction === 'member-tags'")
                                        el-form-item(
                                            :label="$t('Allowed member tags')"
                                            size="mini"
                                        )
                                            el-select.is-full-width(
                                                ref="memberTagSelect"
                                                multiple
                                                clearable
                                                filterable
                                                allow-create
                                                default-first-option
                                                value-key="id"
                                                :placeholder="$t('Select tags')"
                                                v-model="restrictionMemberTags"
                                                @change="$refs.memberTagSelect.blur()"
                                            )
                                                el-option(
                                                    v-for="tag in memberTagOptions"
                                                    :key="tag.id"
                                                    :label="tag.name"
                                                    :value="tag"
                                                ) {{ tag.name }}
                            .setting.gc-position
                                InputField(
                                    :section="editingContainer"
                                    :config="positionFieldConfig"
                                    :content="getContainerSettingValues(editingContainer)"
                                    :options="pageStore.options"
                                    :edit="true"
                                    size="mini"
                                    @change="onContainerSettingChange"
                                )
                            .setting(
                                v-for="setting in containerSettings"
                                :key="setting.key"
                                v-if="checkSettingConditions(setting)"
                                :class="setting.key"
                            )
                                InputField(
                                    :section="editingContainer"
                                    :config="setting"
                                    :content="getContainerSettingValues(editingContainer)"
                                    :options="pageStore.options"
                                    :edit="true"
                                    size="mini"
                                    @change="onContainerSettingChange"
                                )
            .drawer-footer
                el-button(
                    round
                    size="mini"
                    icon="el-icon-fa-times fas"
                    @click="settingsDrawerVisible = false"
                ) {{ $t('Dismiss') }}
                el-button(
                    round
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-check fas"
                    :loading="savingContainerSettings"
                    @click="saveContainerSettings"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Global Contents": "Globale Inhalte",
        "Global Content": "Globaler Inhalt",
        "Settings": "Einstellungen",
        "Position": "Position",
        "Add Global Content": "Globalen Inhalt hinzufügen",
        "Could not load global contents.": "Globale Inhalte konnten nicht geladen werden.",
        "Could not create global content.": "Globaler Inhalt konnte nicht erstellt werden.",
        "Could not delete global content.": "Globaler Inhalt konnte nicht gelöscht werden.",
        "Could not save settings.": "Einstellungen konnten nicht gespeichert werden.",
        "Global content deleted.": "Globaler Inhalt gelöscht.",
        "Settings saved.": "Einstellungen gespeichert.",
        "Accessible by": "Zugänglich von",
        "Everybody": "Jedem",
        "Members": "Mitgliedern",
        "Guests": "Gästen",
        "Any": "Alle",
        "Select tags": "Tags auswählen",
        "Allowed member tags": "Erlaubte Mitglieder-Tags"
    }
}
</i18n>

<script>

import _ from 'lodash'
import uniqid from 'uniqid'
import { adminClient as apolloClient } from '@/store/apollo'

import Area from '@/components/Page/Area'
import InputField from '@/components/Page/Sections/Partials/InputField'
import SectionApi from '@/store/api/cms/section'
import GlobalContentsSectionsGql from '@/store/graphql/cms/queries/GlobalContentsSections.gql'

import pinia from '@/pinia'
import { checkRestrictions } from '@/helpers'
import { useSettingsStore, useSetupStore, usePageStore } from '@/stores'

export default {
    name: 'AbonementGlobalContents',
    components: {
        Area,
        InputField
    },
    data() {
        return {
            loading: true,
            initialized: false,
            dragging: false,
            creatingContainer: false,
            savingContainerSettings: false,
            settingsDrawerVisible: false,
            editingContainer: null,
            restrictions: [
                {
                    key: 'everybody',
                    value: null,
                    name: this.$t('Everybody')
                },
                {
                    key: 'members',
                    value: 'members',
                    name: this.$t('Members')
                },
                {
                    key: 'guests',
                    value: 'guests',
                    name: this.$t('Guests')
                }
            ],
        }
    },
    watch: {
        restriction(value) {
            if (value === 'member-tags') {
                let index = this.restrictions.findIndex(role => role.key === 'members')
                if (index >= 0) {
                    this.$set(this.restrictions[index], 'key', 'member-tags')
                    this.$set(this.restrictions[index], 'value', 'member-tags')
                }
            } else {
                let index = this.restrictions.findIndex(role => role.key === 'member-tags')
                if (index >= 0) {
                    this.$set(this.restrictions[index], 'key', 'members')
                    this.$set(this.restrictions[index], 'value', 'members')
                }
            }
        }
    },
    computed: {
        activeCms() {
            return useSetupStore().activeCms
        },
        enabledFeatures() {
            return _.get(useSetupStore().activeCms, 'config.template.features') || []
        },
        effectiveExtensions() {
            return useSetupStore().effectiveExtensions
        },
        restriction: {
            get() {
                return _.get(this.editingContainer, 'restriction') || null
            },
            set(value) {
                if (this.editingContainer) {
                    this.$set(this.editingContainer, 'restriction', value)
                }
            }
        },
        memberTagOptions() {
            return _.get(this.pageStore.options, 'extensions.restrictedArea.memberTag.items') || []
        },
        restrictionMemberTags: {
            get() {
                let tags = _.get(this.editingContainer, 'restrictionMemberTags') || []
                return tags.map(function (tag) {
                    if (String(tag.id).startsWith('new:')) return tag.name
                    return tag
                })
            },
            set(tags) {
                if (!this.editingContainer) return

                let memberTags = []
                tags.forEach(function (tag) {
                    if (typeof tag === 'string' || tag instanceof String) {
                        memberTags.push({ id: uniqid('new:'), name: tag })
                    } else {
                        memberTags.push(tag)
                    }
                })

                this.$set(this.editingContainer, 'restrictionMemberTags', memberTags)
            }
        },
        form() {
            return useSettingsStore().form
        },
        pageStore() {
            return usePageStore()
        },
        globalContentsConfig() {
            return _.get(this.form, 'config.template.globalContents')
                || _.get(this.activeCms, 'config.template.globalContents')
                || {}
        },
        positions() {
            return _.get(this.globalContentsConfig, 'positions') || []
        },
        defaultPosition() {
            let first = _.get(this.positions, '[0]')
            return _.get(first, 'key', 'floating-bottom-right')
        },
        templateSections() {
            return _.get(this.activeCms, 'config.template.sections') || []
        },
        containerSettings() {
            return _.get(this.globalContentsConfig, 'containerSettings') || []
        },
        positionFieldConfig() {
            return {
                key: 'gc-position',
                name: this.$t('Position'),
                type: 'select',
                options: this.positions.map(function (position) {
                    return {
                        key: position.key,
                        value: position.value,
                    }
                })
            }
        },
        containers() {
            return _.get(this.pageStore.form, 'sections') || []
        },
        childSectionOptions() {
            let allowedKeys = (_.get(this.globalContentsConfig, 'sections') || []).map(function (section) {
                return section.key
            })
            let options = []

            allowedKeys.forEach(function (key) {
                let config = this.templateSections.find(function (item) {
                    return item.key === key
                })
                if (config) {
                    options.push({ is: 'section', ...config })
                }
            }.bind(this))

            return options
        },
    },
    methods: {
        getPositionLabel(container) {
            let area = container.area || this.defaultPosition
            let position = this.positions.find(function (p) { return p.key === area })
            return position ? position.value : area
        },
        getChildAreaConfig(container) {
            let gridArea = _.get(container, 'gridAreas[0]')
            if (!gridArea) return { key: 'main', sections: [], grids: [] }

            return {
                key: gridArea.key || 'main',
                name: '',
                sections: this.childSectionOptions,
                grids: []
            }
        },
        getChildSectionsAddress(container) {
            let containerIndex = this.containers.indexOf(container)
            return [
                _.get(container, 'gridAreas[0].key') || 'main',
                'sections.' + containerIndex + '.gridAreas.0.gridItems'
            ]
        },
        checkSettingConditions(setting) {
            let conditions = _.get(setting, 'conditions')
            if (!conditions || !conditions.length) return true

            let settings = this.editingContainer ? this.getContainerSettingValues(this.editingContainer) : {}
            // eslint-disable-next-line no-unused-vars
            let enabledFeatures = this.enabledFeatures
            let valid = true

            try {
                conditions.forEach(function (condition) {
                    if (!valid) return
                    if (condition.type === 'eval') {
                        valid = eval(condition.condition)
                    }
                })
            } catch (error) {
                console.error(error)
                valid = false
            }

            return valid
        },
        getContainerSettingValues(container) {
            let values = {}
            let sectionSettings = _.get(container, 'settings') || []

            sectionSettings.forEach(function (setting) {
                if (setting.options != null) {
                    values[setting.key] = setting.options
                } else {
                    values[setting.key] = setting.value
                }
            })

            this.containerSettings.forEach(function (configSetting) {
                if (values[configSetting.key] !== undefined) return

                // apply default from options[].default for select fields
                if (configSetting.options && configSetting.options.length) {
                    let defaultOption = configSetting.options.find(function (option) {
                        return option.default === true
                    })
                    if (defaultOption) {
                        values[configSetting.key] = defaultOption.key
                        return
                    }
                }

                // apply default from value field
                if (configSetting.value != null) {
                    values[configSetting.key] = configSetting.value
                }
            })

            // include position as a virtual setting
            values['gc-position'] = container.area || this.defaultPosition

            return values
        },
        onContainerSettingChange(data) {
            if (!this.editingContainer) return

            let key = _.get(data, 'key')
            let value = _.get(data, 'value')

            // handle position separately — it's the area field, not a setting
            if (key === 'gc-position') {
                this.updateContainerPosition(this.editingContainer, value)
                return
            }

            // update the settings array on the container
            let settings = _.cloneDeep(this.editingContainer.settings || [])
            let existing = settings.find(function (s) { return s.key === key })
            if (existing) {
                existing.value = value
            } else {
                settings.push({ key: key, value: value })
            }
            this.$set(this.editingContainer, 'settings', settings)
        },
        addContainer() {
            let cmsId = _.get(this.activeCms, 'id')
            if (!cmsId) return

            this.creatingContainer = true

            let containerData = {
                key: 'global-content',
                name: this.$t('Global Content'),
                type: 'global-content',
                area: this.defaultPosition,
                gridAreas: [{
                    id: uniqid('new:'),
                    key: 'main',
                    name: 'Main',
                    gridItems: []
                }],
                settings: [],
            }

            SectionApi.create(containerData, this.activeCms)
                .then(function (newSection) {
                    let sections = _.cloneDeep(this.containers)
                    sections.push(newSection)
                    this.pageStore.form.sections = sections
                    this.creatingContainer = false
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                    this.creatingContainer = false
                    this.$notify.error({
                        title: this.$t('Could not create global content.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                }.bind(this))
        },
        deleteContainer(container) {
            if (!container.id || String(container.id).startsWith('new:')) {
                let sections = this.containers.filter(function (section) {
                    return section.id !== container.id
                })
                this.pageStore.form.sections = sections
                return
            }

            SectionApi.delete(container.id)
                .then(function () {
                    let sections = this.containers.filter(function (section) {
                        return section.id !== container.id
                    })
                    this.pageStore.form.sections = sections
                    this.$notify.success({
                        title: this.$t('Global content deleted.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 4000,
                    })
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                    this.$notify.error({
                        title: this.$t('Could not delete global content.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                }.bind(this))
        },
        updateContainerPosition(container, position) {
            container.area = position
            if (container.id && !String(container.id).startsWith('new:')) {
                SectionApi.update({ id: container.id, area: position })
            }
        },
        toggleMemberRestriction(value) {
            if (value === true) this.restriction = 'members'
            else this.restriction = 'member-tags'
        },
        openContainerSettings(container) {
            this.editingContainer = container
            this.settingsDrawerVisible = true
        },
        saveContainerSettings() {
            if (!this.editingContainer) return

            this.savingContainerSettings = true
            let payload = {
                id: this.editingContainer.id,
                settings: this.editingContainer.settings,
                area: this.editingContainer.area,
                restriction: this.editingContainer.restriction || null,
                restrictionMemberTags: this.editingContainer.restrictionMemberTags || [],
            }

            SectionApi.update(payload)
                .then(function () {
                    this.savingContainerSettings = false
                    this.settingsDrawerVisible = false
                    this.$notify.success({
                        title: this.$t('Settings saved.'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 4000,
                    })
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                    this.savingContainerSettings = false
                    this.$notify.error({
                        title: this.$t('Could not save settings.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                }.bind(this))
        },
        fetchSectionOptions() {
            let cmsId = _.get(this.activeCms, 'id')
            if (!cmsId) return

            // the same source the page and menu editors use; the CMS record has no enabledExtensionKeys
            let enabledExtensions = useSetupStore().enabledExtensions
            // Member tags come with a shop too, whether or not restricted-area was sold.
            let effectiveExtensions = _.get(this.activeCms, 'effectiveExtensionKeys') || enabledExtensions
            let shopId = _.get(this.activeCms, 'shop.id')

            let dependencies = [
                this.pageStore.fetchMenuOptions(cmsId),
                this.pageStore.fetchTagOptions(cmsId),
                this.pageStore.fetchFileTagOptions(cmsId),
            ]

            if (effectiveExtensions.includes('restricted-area')) {
                dependencies.push(this.pageStore.fetchRestrictedAreaMemberTagOptions(cmsId))
            }
            if (enabledExtensions.includes('event-manager') && checkRestrictions('ex:evnt')) {
                dependencies.push(this.pageStore.fetchEventManagerCategoryOptions(cmsId))
            }
            if (enabledExtensions.includes('shop') && checkRestrictions('ex:shop') && shopId) {
                dependencies.push(this.pageStore.fetchShopCategoryOptions(shopId))
                dependencies.push(this.pageStore.fetchShopTagOptions(shopId))
                dependencies.push(this.pageStore.fetchShopPropertyOptions(shopId))
            }

            return Promise.all(dependencies)
        },
        fetchGlobalContentsSections() {
            let cmsId = _.get(this.activeCms, 'id')
            if (!cmsId) return Promise.reject('No CMS ID')

            this.loading = true

            return apolloClient
                .query({
                    query: GlobalContentsSectionsGql,
                    variables: { cms_id: parseInt(cmsId) },
                    fetchPolicy: 'network-only'
                })
                .then(function (response) {
                    let sections = _.get(response, 'data.CP_GlobalContentsSections') || []

                    this.pageStore.isGlobalContentsMode = true
                    this.pageStore.form = {
                        id: null,
                        sections: _.cloneDeep(sections)
                    }

                    this.initialized = true
                    this.loading = false
                }.bind(this))
                .catch(function (error) {
                    console.error(error)
                    this.loading = false
                    this.$notify.error({
                        title: this.$t('Could not load global contents.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    })
                }.bind(this))
        }
    },
    mounted() {
        this.fetchSectionOptions()
        this.fetchGlobalContentsSections()
    },
    beforeRouteEnter(to, from, next) {
        next(function (vm) {
            let globalContents = _.get(vm.form, 'config.template.globalContents')
                || _.get(vm.activeCms, 'config.template.globalContents')

            if (!globalContents) {
                next({ name: 'settings-form' })
            } else {
                next()
            }
        })
    },
    beforeDestroy() {
        this.pageStore.isGlobalContentsMode = false
        this.pageStore.form = {
            title: '',
            key: '',
            view: '',
            restriction: '',
            category: null,
            sections: [],
            categoryFilters: [],
            tags: [],
            metas: [],
            created_at: null,
            publish_start: null,
            publish_end: null
        }
    }
}
</script>

<style lang="sass" scoped>
.abonement-global-contents
    min-height: 200px

.containers
    display: flex
    flex-direction: column
    gap: 24px

.container-item
    &::v-deep
        & > .area-title-bar
            padding-right: 0.7rem

.container-body
    padding: 0
    &::v-deep
        section.section > .content-wrappers
            padding: 8px 12px 12px
        .page-area > .sticky-container > .area-title-bar
            display: none

.add-container
    margin-top: 8px
</style>
